import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const TIMER_STORAGE_KEY = 'bp-cart-timer-ends-at';

/** @returns {number | null} */
function readTimerEnd() {
  try {
    const value = Number(sessionStorage.getItem(TIMER_STORAGE_KEY));
    return value > 0 ? value : null;
  } catch {
    return null;
  }
}

/** @param {number | null} value */
function writeTimerEnd(value) {
  try {
    if (value) sessionStorage.setItem(TIMER_STORAGE_KEY, String(value));
    else sessionStorage.removeItem(TIMER_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode etc.) — the timer still runs for this page view.
  }
}

/**
 * "We can only reserve your cart for 04:55" countdown.
 *
 * Starts the first time the drawer opens and keeps its end time in sessionStorage, so it carries on
 * across page loads instead of jumping back to the start. When it runs out it starts again the
 * next time the drawer is open. It only ticks while the drawer is open; the rest of the time the
 * saved end time is enough to show the right value on the next open.
 */
class BpCartTimer extends HTMLElement {
  /** @type {number | undefined} */
  #interval;
  /** @type {number | null} */
  #fallbackEnd = null;

  connectedCallback() {
    this.drawer = this.closest('theme-drawer');
    this.drawer?.addEventListener('theme-drawer:open', this.#start);
    this.drawer?.addEventListener('theme-drawer:close', this.#stop);

    if (this.drawer?.hasAttribute('open')) {
      this.#start();
    } else {
      const end = this.#getEnd();
      const now = Date.now();
      this.#render(end && end > now ? end - now : this.#duration);
    }
  }

  disconnectedCallback() {
    this.drawer?.removeEventListener('theme-drawer:open', this.#start);
    this.drawer?.removeEventListener('theme-drawer:close', this.#stop);
    this.#stop();
  }

  get #duration() {
    return (Number(this.dataset.minutes) || 5) * 60 * 1000;
  }

  #getEnd() {
    return readTimerEnd() ?? this.#fallbackEnd;
  }

  /** @param {number | null} value */
  #setEnd(value) {
    this.#fallbackEnd = value;
    writeTimerEnd(value);
  }

  #start = () => {
    this.#tick();
    if (this.#interval === undefined) this.#interval = window.setInterval(this.#tick, 1000);
  };

  #stop = () => {
    if (this.#interval !== undefined) window.clearInterval(this.#interval);
    this.#interval = undefined;
  };

  #tick = () => {
    const now = Date.now();
    let end = this.#getEnd();

    // First open, or it ran out: start a fresh countdown.
    if (!end || end <= now) {
      end = now + this.#duration;
      this.#setEnd(end);
    }

    this.#render(end - now);
  };

  /** @param {number} remainingMs */
  #render(remainingMs) {
    const clock = this.querySelector('.bp-timer__clock');
    if (!clock) return;

    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    clock.textContent = `${minutes}:${seconds}`;
  }
}

/**
 * One-click upsell inside the cart drawer. Adds the product, then hands the re-rendered drawer
 * to the theme through a CartLinesUpdateEvent, the same way the product form does, so the drawer,
 * cart bubble and totals all refresh together.
 */
class BpCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('.bp-upsell__button');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = Number(this.dataset.variantId);
    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#showError('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      // kaching-cart=true is Kaching Bundles' own opt-out flag: without it, on a product page Kaching
      // would merge the currently selected bundle into this request.
      const response = await fetch(
        `${Theme.routes.cart_add_url}?kaching-cart=true`,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const result = await response.json();
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
        (res) => res.json()
      );

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({ error: result.description || result.message || 'Add to cart failed', code: 'INVALID' })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'bp-cart-upsell', didError: true },
        });
        this.#showError(result.description || result.message || '');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          sections: result.sections,
          source: 'bp-cart-upsell',
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      // On success the drawer re-renders without this card; this only matters after an error.
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('.bp-upsell__error');
    if (!error) return;
    error.textContent = message;
    error.toggleAttribute('hidden', !message);
  }
}

/* ------------------------------------------------------------------------------------------------
 * Kaching bundles (snippets/biopaw-cart-bundle.liquid) and the size picker on every row.
 *
 * Kaching's discount picks the deal from the number of bundle jars, but it leaves the bundle's gift
 * lines in the cart when the deal no longer covers them (they then cost full price), and it drops
 * them when the bundle's jar lines are removed (e.g. for a size swap). So after every cart change:
 *   - any bundle gift that is no longer free is removed (and remembered), and
 *   - the gifts of the bundle's current deal are added when missing. Each deal's gifts come from
 *     Kaching's own settings on the product page (e.g. raising Buy 2 Get 1 to 5 jars adds the
 *     90-Day Chart); gifts seen earlier in the cart are the fallback.
 *
 * Speed: each change shows straight away (new size and estimated price, new quantity, removed card)
 * and only the prices pulse while it saves. Each change makes as few requests as possible, and the
 * drawer is re-rendered from the last request's own response (Shopify's `sections` option) instead of
 * being fetched again. A background check a moment later catches anything Kaching changes late.
 * ---------------------------------------------------------------------------------------------- */

const GIFT_STORE_KEY = 'bp-bundle-gifts';
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** @param {any} item */
function kachingData(item) {
  try {
    return JSON.parse(item?.properties?.__kaching_bundles || 'null');
  } catch {
    return null;
  }
}

function readGiftStore() {
  try {
    return JSON.parse(sessionStorage.getItem(GIFT_STORE_KEY) || '{}');
  } catch {
    return {};
  }
}

/** @param {Record<string, any[]>} store */
function writeGiftStore(store) {
  try {
    sessionStorage.setItem(GIFT_STORE_KEY, JSON.stringify(store));
  } catch {
    // Storage unavailable: gifts can't be put back after being removed, everything else still works.
  }
}

/** The most recent full cart we've seen (from /cart.js or /cart/update.js). */
let lastCart = /** @type {any} */ (null);

function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(Boolean);
}

/**
 * @param {string} url
 * @param {Record<string, any>} body
 * @param {boolean} [withSections] - Also return the re-rendered cart drawer
 */
async function postJSON(url, body, withSections = false) {
  const ids = withSections ? sectionIds() : [];
  if (ids.length) Object.assign(body, { sections: ids.join(','), sections_url: window.location.pathname });

  const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
  const result = await response.json();
  if (!response.ok || result.status) {
    throw new Error(result.description || result.message || `Cart request failed (${response.status})`);
  }
  return result;
}

async function getCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  lastCart = await response.json();
  return lastCart;
}

/**
 * kaching-cart=true stops Kaching merging the product page's selected bundle into these adds.
 * @param {any[]} items
 * @param {boolean} [withSections]
 */
const addItems = (items, withSections) =>
  postJSON(`${Theme.routes.cart_add_url}?kaching-cart=true`, { items }, withSections);

/**
 * Returns the whole cart (with `sections` when asked for).
 * @param {Record<string, number>} updates
 * @param {boolean} [withSections]
 */
async function updateLines(updates, withSections) {
  lastCart = await postJSON(`${Theme.routes.cart_update_url}.js`, { updates }, withSections);
  return lastCart;
}

/**
 * The last cart we saw if it still has all of these lines, otherwise a fresh one. A line key covers
 * the variant and its properties, so a cart that has the keys is good enough to copy lines from.
 * @param {string[]} keys
 */
async function cartWith(keys) {
  const known = new Set(lastCart?.items?.map((/** @type {any} */ item) => item.key));
  return keys.every((key) => known.has(key)) ? lastCart : getCart();
}

/* Each Kaching deal's gifts ("Buy 3 Get 2 Free" -> course + chart), read from Kaching's settings on
   the product page and kept in localStorage, keyed by Kaching's deal block id (the `deal` property on
   the bundle's lines). */
const DEALS_STORE_KEY = 'bp-kaching-deals';

function readDeals() {
  try {
    return JSON.parse(localStorage.getItem(DEALS_STORE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Variant to add for a Kaching gift: the one Kaching names, or the gift product's first available one.
 * @param {Document} root
 * @param {any} gift - A `freeGifts` entry from Kaching's deal settings
 */
function giftVariant(root, gift) {
  const numericId = (/** @type {any} */ gid) => Number(String(gid ?? '').split('/').pop()) || null;
  if (gift.variantGIDs?.length) return numericId(gift.variantGIDs[0]);

  const productId = String(gift.productGID ?? '').split('/').pop();
  try {
    const script = root.querySelector(`script.kaching-bundles-product[data-product-id="${productId}"]`);
    const product = JSON.parse(script?.textContent || 'null');
    const variant =
      product?.variants?.find((/** @type {any} */ v) => v.availableForSale ?? v.available) ?? product?.variants?.[0];
    return numericId(variant?.id);
  } catch {
    return null;
  }
}

/**
 * Remembers the deals on a page that has Kaching's deal block (the product page).
 * @param {Document} root
 */
function captureDeals(root) {
  const deals = readDeals();
  let found = false;

  for (const script of root.querySelectorAll('script.kaching-bundles-deal-block-settings')) {
    try {
      const settings = JSON.parse(script.textContent || '');
      if (!settings?.nanoId || !Array.isArray(settings.dealBars)) continue;
      deals[settings.nanoId] = settings.dealBars.map((/** @type {any} */ bar) => ({
        title: bar.title,
        jars: bar.quantity ?? (bar.buyQuantity ?? 0) + (bar.getQuantity ?? 0),
        gifts: (bar.freeGifts ?? [])
          .filter((/** @type {any} */ gift) => gift.giftType === 'product')
          .map((/** @type {any} */ gift) => ({ gift: gift.id, variantId: giftVariant(root, gift), quantity: gift.quantity || 1 }))
          .filter((/** @type {any} */ gift) => gift.variantId),
      }));
      found = true;
    } catch {
      // Not the format we know: gifts seen in the cart are still used.
    }
  }

  if (found) {
    try {
      localStorage.setItem(DEALS_STORE_KEY, JSON.stringify(deals));
    } catch {
      // Storage unavailable: gifts seen in the cart are still used.
    }
  }
}

captureDeals(document);

let dealsFetched = false;

/**
 * If a bundle's deals haven't been seen in this browser (e.g. the cart was filled on another device),
 * reads them from the bundle's product page once.
 * @param {any} cart
 */
async function ensureDeals(cart) {
  if (dealsFetched) return;
  const deals = readDeals();
  const unknown = [...bundlesIn(cart).values()].find(
    (bundle) => bundle.jars.length > 0 && !deals[kachingData(bundle.jars[0])?.deal]
  );
  if (!unknown?.jars[0]?.url) return;

  dealsFetched = true;
  const html = await fetch(unknown.jars[0].url).then((response) => response.text());
  captureDeals(new DOMParser().parseFromString(html, 'text/html'));
}

/**
 * The cart's bundles: each Kaching bundle id with its jar lines and gift lines.
 * @param {any} cart
 */
function bundlesIn(cart) {
  /** @type {Map<string, {jars: any[], gifts: {item: any, kb: any}[]}>} */
  const bundles = new Map();
  for (const item of cart.items) {
    const kb = kachingData(item);
    if (!kb?.id) continue;
    const bundle = bundles.get(kb.id) ?? { jars: [], gifts: [] };
    if (kb.gift) bundle.gifts.push({ item, kb });
    else if (kb.main) bundle.jars.push(item);
    bundles.set(kb.id, bundle);
  }
  return bundles;
}

/**
 * Name of the deal on a bundle's jars ("Buy 2 Get 1 Free"), or the jar count if it has none.
 * @param {any[]} jars
 */
function dealOf(jars) {
  for (const item of jars) {
    const title =
      item.line_level_discount_allocations?.[0]?.discount_application?.title ?? item.discounts?.[0]?.title;
    if (title) return String(title);
  }
  return `jars:${jars.reduce((sum, item) => sum + item.quantity, 0)}`;
}

/**
 * The deal bar that applies to a bundle: the one named like the discount on its jars, otherwise the
 * biggest one its jar count reaches.
 * @param {any[] | undefined} bars
 * @param {string} deal
 * @param {any[]} jars
 */
function dealBar(bars, deal, jars) {
  if (!bars) return null;
  const named = bars.find((bar) => bar.title === deal);
  if (named) return named;
  const count = jars.reduce((sum, item) => sum + item.quantity, 0);
  return bars.filter((bar) => bar.jars > 0 && bar.jars <= count).sort((a, b) => b.jars - a.jars)[0] ?? null;
}

/**
 * Works out what to change so each bundle's gifts match its deal. Also remembers each bundle's gifts
 * (so they can be put back) and which deals don't cover a gift (so it isn't put back only to be
 * charged and removed again).
 * @param {any} cart - A full cart (/cart.js shape)
 * @param {Set<string>} handled - Gifts already removed or put back during this change (not retried)
 */
function planGiftFixes(cart, handled = new Set()) {
  const store = readGiftStore();
  const deals = readDeals();
  const bundles = bundlesIn(cart);

  for (const id of Object.keys(store)) {
    if (!bundles.has(id)) delete store[id];
  }

  /** @type {Record<string, number>} */
  const updates = {};
  const adds = [];

  for (const [id, bundle] of bundles) {
    const remembered = store[id] ?? [];
    for (const { item, kb } of bundle.gifts) {
      if (!remembered.some((/** @type {any} */ gift) => gift.gift === kb.gift)) {
        remembered.push({ gift: kb.gift, id: item.variant_id, quantity: item.quantity, properties: item.properties });
      }
    }
    store[id] = remembered;

    if (bundle.jars.length === 0) {
      for (const { item } of bundle.gifts) updates[item.key] = 0;
      delete store[id];
      continue;
    }

    const deal = dealOf(bundle.jars);
    for (const { item, kb } of bundle.gifts) {
      if (item.final_line_price > 0) {
        updates[item.key] = 0;
        handled.add(`${id}:${kb.gift}`);
        const gift = remembered.find((/** @type {any} */ entry) => entry.gift === kb.gift);
        if (gift) gift.notFreeWith = [...new Set([...(gift.notFreeWith ?? []), deal])];
      }
    }

    const dealActive = bundle.jars.some((item) => item.final_line_price === 0 && item.original_line_price > 0);
    if (!dealActive) continue;

    const main = kachingData(bundle.jars[0]) ?? {};
    const bar = dealBar(deals[main.deal], deal, bundle.jars);
    const notFree = (/** @type {string} */ giftId) =>
      remembered.find((/** @type {any} */ entry) => entry.gift === giftId)?.notFreeWith?.includes(deal);

    if (bar) {
      // The deal's own gifts, with the same properties Kaching gives them
      for (const gift of bar.gifts) {
        const key = `${id}:${gift.gift}`;
        const inCart = bundle.gifts.some(({ item }) => item.variant_id === gift.variantId);
        if (!inCart && !handled.has(key) && !notFree(gift.gift)) {
          const properties = { __kaching_bundles: JSON.stringify({ id, deal: main.deal, v: main.v, gift: gift.gift }) };
          adds.push({ id: gift.variantId, quantity: gift.quantity, properties });
          handled.add(key);
        }
      }
      continue;
    }

    // Deal settings not known in this browser: put back gifts this bundle had before
    for (const gift of remembered) {
      const key = `${id}:${gift.gift}`;
      const inCart = bundle.gifts.some(({ kb }) => kb.gift === gift.gift);
      if (!inCart && !handled.has(key) && !gift.notFreeWith?.includes(deal)) {
        adds.push({ id: gift.id, quantity: gift.quantity, properties: gift.properties });
        handled.add(key);
      }
    }
  }

  writeGiftStore(store);
  return { updates, adds };
}

/**
 * @typedef {{ cart?: any, sections?: Record<string, string> }} CartState
 *   `cart` is the full cart when the last request returned one; `sections` is the drawer HTML to show.
 */

/**
 * Brings the gifts in line after a change, starting from the cart that change returned.
 * @param {CartState} state
 * @returns {Promise<CartState>}
 */
async function settleGifts(state) {
  const handled = new Set();

  for (let pass = 0; pass < 3 && state.cart; pass++) {
    const { updates, adds } = planGiftFixes(state.cart, handled);
    const hasUpdates = Object.keys(updates).length > 0;
    if (!hasUpdates && adds.length === 0) break;

    if (hasUpdates) {
      const cart = await updateLines(updates, adds.length === 0);
      state = { cart, sections: cart.sections ?? state.sections };
    }
    if (adds.length) {
      // An add doesn't return the whole cart; the background check confirms the gifts came back free.
      const result = await addItems(adds, true);
      state = { sections: result.sections };
    }
  }

  return state;
}

/** @param {Record<string, string> | undefined} sections */
function itemCountFrom(sections) {
  for (const html of Object.values(sections ?? {})) {
    const match = /ref="cartItemCount"[^>]*>\s*(\d+)/.exec(html);
    if (match) return Number(match[1]);
  }
  return undefined;
}

/**
 * Hands the new drawer HTML to the theme the same way its own cart updates do, so the drawer, cart
 * bubble and totals all refresh together.
 * @param {CartState} state
 * @param {string} source
 */
async function renderCart(state, source) {
  if (!state.sections) {
    await refreshCart(source);
    return;
  }
  const cart = state.cart?.items ? state.cart : null;

  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
  /** @type {any} */ (event).bpInternal = true;
  document.dispatchEvent(event);

  deferred.resolve({
    cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
    detail: {
      items: cart?.items ?? [],
      itemCount: cart?.item_count ?? itemCountFrom(state.sections) ?? lastCart?.item_count,
      sections: state.sections,
      source,
      didError: false,
    },
  });
}

/**
 * Re-renders the drawer from the current cart.
 * @param {string} source
 */
async function refreshCart(source) {
  const ids = sectionIds();
  if (ids.length === 0) return;

  const [cart, sections] = await Promise.all([
    getCart(),
    fetch(`${window.location.pathname}?sections=${ids.join(',')}`).then((response) => response.json()),
  ]);
  renderCart({ cart, sections }, source);
}

// Only one cart change at a time; the +/-, ×, size pickers and Checkout pause while one is saving.
let cartBusy = false;

/**
 * Checks the cart and fixes the bundle gifts if they're out of step. Re-renders only if something
 * changed.
 * @param {string} source
 */
async function repairGifts(source) {
  if (cartBusy) return;
  const cart = await getCart();
  await ensureDeals(cart).catch(() => {});
  if (cartBusy) return;

  const { updates, adds } = planGiftFixes(cart);
  if (Object.keys(updates).length === 0 && adds.length === 0) return;

  cartBusy = true;
  const drawer = document.getElementById('cart-drawer');
  drawer?.classList.add('bp-cart-busy');
  try {
    await renderCart(await settleGifts({ cart }), source);
  } finally {
    cartBusy = false;
    drawer?.classList.remove('bp-cart-busy');
  }
}

/**
 * Runs one change from the drawer: the caller has already shown the result on screen; this saves it,
 * fixes the gifts and re-renders the drawer.
 * @param {HTMLElement} card - The bundle card or row being changed (its prices pulse while saving)
 * @param {() => Promise<CartState>} change - Makes the change; its last request asks for `sections`
 * @param {() => void} [onError] - Undo anything the drawer's re-render won't put back
 */
async function runCartChange(card, change, onError) {
  if (cartBusy) return;
  cartBusy = true;

  const drawer = document.getElementById('cart-drawer');
  drawer?.classList.add('bp-cart-busy');
  card.classList.add('is-updating');
  card.setAttribute('aria-busy', 'true');

  try {
    let state = await change();
    if (state.cart) state = await settleGifts(state);
    await renderCart(state, 'bp-cart');
  } catch (error) {
    console.error('[biopaw-cart]', error);
    onError?.();
    // Show the cart as it really is, rather than what was shown while saving.
    await refreshCart('bp-cart').catch(() => {});
  } finally {
    cartBusy = false;
    drawer?.classList.remove('bp-cart-busy');
    card.classList.remove('is-updating');
    card.removeAttribute('aria-busy');
  }

  // Kaching can tidy a bundle's gifts a moment after a change; check once more and repair if needed.
  await sleep(1000);
  await repairGifts('bp-cart-sync').catch(() => {});
}

/**
 * Formats like Liquid's `money` filter. `sample` is that filter's output for 1234.00 ("$1,234.00").
 * @param {number} cents
 * @param {string} [sample]
 * @param {boolean} [noDecimals] - Whole amount without pennies ("$119"), like `money_without_trailing_zeros`
 */
function formatMoney(cents, sample = '', noDecimals = false) {
  const match = sample.match(/^(.*?)1(\D?)234(?:(\D)00)?(.*)$/);
  if (!match) return noDecimals ? String(Math.round(cents / 100)) : (cents / 100).toFixed(2);

  const [, prefix, thousands, decimal, suffix] = match;
  const showDecimals = Boolean(decimal) && !noDecimals;
  const [whole, fraction] = (showDecimals ? (cents / 100).toFixed(2) : String(Math.round(cents / 100))).split('.');
  const grouped = (whole ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, thousands ?? '');
  return `${prefix}${grouped}${showDecimals ? decimal + fraction : ''}${suffix}`;
}

/** @param {HTMLElement} element @param {string} name */
function readJSON(element, name) {
  try {
    return JSON.parse(element.dataset[name] || 'null');
  } catch {
    return null;
  }
}

/**
 * Shows the bundle's price for a new size straight away. The full price is exact; the price after the
 * deal is estimated from the paid jars' price difference until the cart confirms it.
 * @param {HTMLElement} bundle
 * @param {HTMLOptionElement | null} from
 * @param {HTMLOptionElement | undefined} to
 */
function previewBundlePrice(bundle, from, to) {
  const oldPrice = Number(from?.dataset.price);
  const newPrice = Number(to?.dataset.price);
  if (!to || !Number.isFinite(oldPrice) || !Number.isFinite(newPrice)) return;

  const total = Number(bundle.dataset.total) || 0;
  const paidUnits = Number(bundle.dataset.paidUnits) || 0;
  const final = Math.max(0, Number(bundle.dataset.final) + (newPrice - oldPrice) * paidUnits);
  const compare = Math.max(newPrice, Number(to.dataset.compare) || 0) * total;
  // Like `money_without_trailing_zeros`: "$249" but "$129.99"
  const money = (/** @type {number} */ cents) => formatMoney(cents, bundle.dataset.moneySample, cents % 100 === 0);

  const finalElement = bundle.querySelector('.bp-line__final');
  const compareElement = bundle.querySelector('.bp-line__compare');
  const saveElement = bundle.querySelector('.bp-line__save');
  const saveAmount = bundle.querySelector('.bp-line__save-amount');
  if (finalElement) finalElement.textContent = money(final);
  if (compareElement) compareElement.textContent = money(compare);
  // "You save $119": whole amounts, rounded down, as in the Liquid
  const saving = Math.floor((compare - final) / 100) * 100;
  if (saveAmount) saveAmount.textContent = formatMoney(saving, bundle.dataset.moneySample, true);
  compareElement?.toggleAttribute('hidden', compare <= final);
  saveElement?.toggleAttribute('hidden', saving <= 0);
}

/**
 * New size for a bundle: remove its jars and gifts in one request, then add the new jars and the same
 * gifts in one request (Kaching drops the gifts anyway once the old jars go). The drawer is rendered
 * from the add's response.
 * @param {HTMLElement} bundle
 * @param {number} variantId
 * @returns {Promise<CartState>}
 */
async function swapBundleSize(bundle, variantId) {
  const jarKeys = readJSON(bundle, 'jarKeys') ?? [];
  const memberKeys = readJSON(bundle, 'memberKeys') ?? [];
  const cart = await cartWith(memberKeys);
  planGiftFixes(cart); // remember the gifts

  const lines = cart.items.filter((/** @type {any} */ item) => memberKeys.includes(item.key));
  const paid = lines.find((/** @type {any} */ item) => item.key === bundle.dataset.paidKey) ?? lines.find((/** @type {any} */ item) => jarKeys.includes(item.key));
  const gifts = lines.filter((/** @type {any} */ item) => kachingData(item)?.gift && item.final_line_price === 0);
  const giftItems = gifts.map((/** @type {any} */ item) => ({ id: item.variant_id, quantity: item.quantity, properties: item.properties }));

  await updateLines(Object.fromEntries(memberKeys.map((/** @type {string} */ key) => [key, 0])));

  try {
    const result = await addItems(
      [{ id: variantId, quantity: Number(bundle.dataset.total), properties: paid?.properties ?? {} }, ...giftItems],
      true
    );
    return { sections: result.sections };
  } catch (error) {
    // The new size couldn't be added (e.g. not enough stock): put the original bundle back.
    const jars = lines.filter((/** @type {any} */ item) => jarKeys.includes(item.key));
    const quantity = jars.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);
    if (paid && quantity) await addItems([{ id: paid.variant_id, quantity, properties: paid.properties }, ...giftItems]);
    throw error;
  }
}

/**
 * New size for a normal row: add the new variant with the same quantity and properties, then remove
 * the old line in a request that returns the whole cart and the re-rendered drawer.
 * @param {HTMLElement} row
 * @param {number} variantId
 * @returns {Promise<CartState>}
 */
async function swapRowSize(row, variantId) {
  const key = row.dataset.key || '';
  const cart = await cartWith([key]);
  const item = cart.items.find((/** @type {any} */ line) => line.key === key);

  await addItems([{ id: variantId, quantity: Number(row.dataset.quantity) || item?.quantity || 1, properties: item?.properties ?? {} }]);
  const updated = await updateLines({ [key]: 0 }, true);
  return { cart: updated, sections: updated.sections };
}

/**
 * @param {HTMLElement} bundle
 * @param {string[]} memberKeys
 */
function removeBundle(bundle, memberKeys) {
  bundle.closest('.bp-line')?.classList.add('is-removing');
  runCartChange(bundle, async () => {
    const store = readGiftStore();
    delete store[bundle.dataset.bundleId || ''];
    writeGiftStore(store);

    const cart = await updateLines(Object.fromEntries(memberKeys.map((key) => [key, 0])), true);
    return { cart, sections: cart.sections };
  });
}

/**
 * Takes a single line of bundle jars to `target` jars (3 = Buy 2 Get 1 Free; 4 = the Halloween Buy 2
 * Get 2 Free). The deal's
 * free gift is then added by settleGifts, and the drawer shows the bundle card.
 * @param {HTMLElement} row
 * @param {string} key
 * @param {number} [target]
 */
function upgradeToThree(row, key, target = 3) {
  const input = row.querySelector('.bp-line__qty input');
  if (input instanceof HTMLInputElement) input.value = String(target);

  return runCartChange(row, async () => {
    const cart = await cartWith([key]);
    await ensureDeals(cart).catch(() => {});
    planGiftFixes(cart); // remember the gifts
    const updated = await updateLines({ [key]: target }, true);
    return { cart: updated, sections: updated.sections };
  });
}

// "+1 Jar, Get 1 FREE" card (snippets/biopaw-cart-upgrade.liquid)
document.addEventListener('click', (event) => {
  const button = /** @type {HTMLElement} */ (event.target).closest?.('[data-bp-upgrade]');
  if (!(button instanceof HTMLElement) || cartBusy) return;
  const row = button.closest('[data-bp-line]');
  if (row instanceof HTMLElement && button.dataset.key) upgradeToThree(row, button.dataset.key, Number(button.dataset.target) || 3);
});

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const bundle = target.closest?.('[data-bp-bundle]');
  if (!(bundle instanceof HTMLElement) || cartBusy) return;

  const jarKeys = readJSON(bundle, 'jarKeys') ?? [];
  const memberKeys = readJSON(bundle, 'memberKeys') ?? [];

  const stepButton = target.closest('[data-bundle-step]');
  if (stepButton instanceof HTMLElement) {
    const next = Number(bundle.dataset.total) + Number(stepButton.dataset.bundleStep);
    if (next <= 0) {
      removeBundle(bundle, memberKeys);
      return;
    }

    const value = bundle.querySelector('.bp-stepper__value');
    if (value) value.textContent = String(next);

    runCartChange(bundle, async () => {
      // Remember the gifts first: if this loses the deal, the gifts that stop being free are removed.
      planGiftFixes(await cartWith(memberKeys));

      /** @type {Record<string, number>} */
      const updates = {};
      // Put every jar on one line; Shopify and Kaching re-split paid and free units.
      for (const key of jarKeys) updates[key] = 0;
      updates[bundle.dataset.paidKey || jarKeys[0]] = next;

      const cart = await updateLines(updates, true);
      return { cart, sections: cart.sections };
    });
    return;
  }

  if (target.closest('[data-bundle-remove]')) removeBundle(bundle, memberKeys);
});

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.classList.contains('bp-variant__select')) return;

  const original = /** @type {HTMLOptionElement | null} */ (select.querySelector('option[selected]'));
  const resetSelect = () => {
    if (original) select.value = original.value;
  };
  if (cartBusy) {
    resetSelect();
    return;
  }

  const option = select.selectedOptions[0];
  const label = select.closest('.bp-variant')?.querySelector('.bp-variant__label');
  if (label && option?.dataset.label) label.textContent = option.dataset.label;

  const variantId = Number(select.value);
  const bundle = select.closest('[data-bp-bundle]');
  const row = select.closest('[data-bp-line]');

  if (bundle instanceof HTMLElement) {
    previewBundlePrice(bundle, original, option);
    runCartChange(bundle, () => swapBundleSize(bundle, variantId), resetSelect);
  } else if (row instanceof HTMLElement) {
    runCartChange(row, () => swapRowSize(row, variantId), resetSelect);
  }
});

/**
 * A single jar the shopper raised to 2 with the row's +/- (or by typing 2): 2 jars costs the same as
 * Buy 2 Get 1 Free, so take it straight to 3. Only from 1 upwards, so coming down from the bundle to 2
 * isn't pushed back up. Rows opt in with data-bp-auto-jars (theme setting "Pressing + on 1 jar…").
 * @param {string[]} singleJarKeys - Keys of the single-jar rows before the change
 */
async function autoFreeJar(singleJarKeys) {
  const cart = await getCart();
  for (const key of singleJarKeys) {
    const item = cart.items.find((/** @type {any} */ line) => line.key === key);
    const bundleId = kachingData(item)?.id;
    const jars = bundleId ? bundlesIn(cart).get(bundleId)?.jars ?? [] : [];
    const total = jars.reduce((/** @type {number} */ sum, /** @type {any} */ line) => sum + line.quantity, 0);
    const row = document.querySelector(`#cart-drawer [data-bp-line][data-key="${CSS.escape(key)}"]`);
    if (item?.quantity === 2 && jars.length === 1 && total === 2 && row instanceof HTMLElement) {
      // Halloween product rows carry data-bp-upgrade-to="4" (Buy 2 Get 2); everything else goes to 3
      await upgradeToThree(row, key, Number(row.dataset.bpUpgradeTo) || 3);
      return true;
    }
  }
  return false;
}

// Changes made elsewhere (the +/- on normal rows, the product page) can also move a bundle in or out of
// its deal, so check the gifts after those too.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (/** @type {any} */ (event).bpInternal || cartBusy) return;
  const singleJarKeys = [...document.querySelectorAll('#cart-drawer [data-bp-auto-jars="1"]')]
    .map((row) => (row instanceof HTMLElement ? row.dataset.key : ''))
    .filter((key) => Boolean(key));
  /** @type {any} */ (event).promise
    ?.then(async () => {
      if (singleJarKeys.length && (await autoFreeJar(/** @type {string[]} */ (singleJarKeys)))) return;
      await repairGifts('bp-cart-sync');
    })
    .catch(() => {});
});

// A cart can reach a page already out of step (e.g. changed before this cart existed), so check it once
// on load when it has items. This also loads the cart, so the first change in the drawer is quicker.
if (document.querySelector('#cart-drawer [data-bp-line], #cart-drawer [data-bp-bundle]')) {
  setTimeout(() => repairGifts('bp-cart-sync').catch(() => {}), 1000);
}

if (!customElements.get('bp-cart-timer')) customElements.define('bp-cart-timer', BpCartTimer);
if (!customElements.get('bp-cart-upsell')) customElements.define('bp-cart-upsell', BpCartUpsell);
